import { Suspense, lazy } from 'react';
import { useSelector } from 'react-redux';
import { useRoutes, useLocation, Navigate } from 'react-router-dom';
// layouts
import DashboardLayout from '../layouts/dashboard';
// components
import LoadingScreen from '../components/LoadingScreen';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();
  const isDashboard = pathname.includes('/dashboard');

  return (
    <Suspense
      fallback={
        <LoadingScreen
          sx={{
            ...(!isDashboard && {
              top: 0,
              left: 0,
              width: 1,
              zIndex: 9999,
              position: 'fixed'
            })
          }}
        />
      }
    >
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  const { isLoggedIn } = useSelector((state) => state.auth);
  return useRoutes(
    !isLoggedIn
      ? [
          {
            path: '/auth',
            children: [
              { path: 'login', element: <Login /> },
              {
                path: 'forgot-password',
                element: <ForgotPassword />
              },
              {
                path: 'change-password',
                element: <ChangePassword />
              },
              { path: 'mail-confirmation', element: <PageMailConfirmation /> },
              { path: 'mail-verified/:hashKey', element: <VerifyCode /> },
              { path: 'verify/:hashKey', element: <PageMailConfirmation /> }
            ]
          },
          {
            path: '/',
            element: isLoggedIn ? <Navigate to="dashboard/posts" replace /> : <Navigate to="auth/login" replace />
          },
          { path: '*', element: <Navigate to="auth/login" replace /> }
        ]
      : [
          // Dashboard Routes
          {
            path: 'dashboard',
            element: <DashboardLayout />,
            children: [
              { path: 'profile', element: <Profile /> },
              { path: 'enquiry', element: <ListEnquiry /> },
              { path: 'refrences', element: <ListRefrences /> },
              {
                path: 'posts',
                children: [
                  { path: '', element: <ListPosts /> },
                  { path: 'new-post', element: <NewPost /> },
                  { path: ':title', element: <NewPost /> }
                ]
              },
              {
                path: 'video',
                children: [
                  { path: '', element: <ListVideo /> },
                  { path: 'new-video', element: <NewVideo /> },
                  { path: ':id', element: <NewVideo /> }
                ]
              },
              {
                path: 'overview',
                children: [
                  { path: '', element: <ListOverwiew /> },
                  { path: ':id', element: <ListOverviewList /> }
                  // { path: ':id/:id', element: <ListOverviewList /> }
                ]
              },
              {
                path: 'guidlines',
                children: [
                  { path: '', element: <ListGuidlines /> },
                  { path: 'new', element: <NewGuidline /> }
                  // { path: 'view/:id', element: <DetailDecision /> }
                ]
              },
              {
                path: 'classifications',
                children: [
                  { path: '', element: <ListClassification /> },
                  { path: 'new', element: <NewClassification /> }
                ]
              },
              {
                path: 'antibiotics',
                children: [
                  { path: '', element: <ListAntibiotics /> },
                  { path: 'new', element: <NewAntibiotic /> }
                ]
              },
              {
                path: 'decisions',
                children: [
                  { path: '', element: <ListDecision /> },
                  { path: 'new', element: <NewDecision /> }
                ]
              },
              {
                path: 'categories',
                children: [{ path: '', element: <ListCategories /> }]
              },
              {
                path: 'subcategories',
                children: [
                  { path: '', element: <ListSubCategories /> },
                  { path: ':id', element: <DetailSubtoSub /> }
                ]
              },
              { path: '', element: <Navigate to="dashboard" replace /> }
            ]
          },

          {
            path: '',
            element: <Navigate to="dashboard/posts" replace />
          },
          {
            path: 'auth/login',
            element: <Navigate to="dashboard/posts" replace />
          },
          { path: '*', element: <NotFound /> }
        ]
  );
}

// IMPORT COMPONENTS

// Dashboard

const NotFound = Loadable(lazy(() => import('../pages/Page404')));
const PageMailConfirmation = Loadable(lazy(() => import('../pages/PageMailConfirmation')));
const Login = Loadable(lazy(() => import('../pages/authentication/Login')));
const ForgotPassword = Loadable(lazy(() => import('../pages/authentication/ForgotPassword')));
const ChangePassword = Loadable(lazy(() => import('../pages/authentication/ChangePassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/authentication/VerifyCode')));

const Dashboard = Loadable(lazy(() => import('../pages/dashboard/Dashboard')));

const ListPosts = Loadable(lazy(() => import('../pages/static-pages/ListPosts')));
const NewPost = Loadable(lazy(() => import('../pages/static-pages/NewPage')));
const Profile = Loadable(lazy(() => import('../pages/profile/Profile')));

const ListEnquiry = Loadable(lazy(() => import('../pages/enquiry/LIstenquiry')));
const ListRefrences = Loadable(lazy(() => import('../pages/refrences/ListRefrences')));

const ListVideo = Loadable(lazy(() => import('../pages/video/ListVideo')));
const NewVideo = Loadable(lazy(() => import('../pages/video/NewVideo')));

const ListGuidlines = Loadable(lazy(() => import('../pages/guidlines/ListGuidlines')));
const ListAntibiotics = Loadable(lazy(() => import('../pages/antibiotics/ListAntibiotics')));
const NewAntibiotic = Loadable(lazy(() => import('../pages/antibiotics/NewAntibiotic')));
const NewClassification = Loadable(lazy(() => import('../pages/classifications/NewClassification')));
const ListDecision = Loadable(lazy(() => import('../pages/decision-making/ListDecision')));
// const DetailDecision = Loadable(lazy(() => import('../pages/decision-making/DetailDecision')));
const NewDecision = Loadable(lazy(() => import('../pages/decision-making/NewDecision')));
const ListClassification = Loadable(lazy(() => import('../pages/classifications/ListClassification')));
const ListOverwiew = Loadable(lazy(() => import('../pages/overview/ListOverview')));
const ListCategories = Loadable(lazy(() => import('../pages/categories/ListCategories')));
const NewGuidline = Loadable(lazy(() => import('../pages/guidlines/NewGuidline')));
const ListOverviewDetail = Loadable(lazy(() => import('../pages/overview/ListOverviewDetail')));
const ListOverviewList = Loadable(lazy(() => import('../pages/overview/ListOverviewList')));
const ListSubCategories = Loadable(lazy(() => import('../pages/subcategories/ListSubCategories')));
const ListSubtoSub = Loadable(lazy(() => import('../pages/subcategories/ListSubtoSub')));
const DetailSubtoSub = Loadable(lazy(() => import('../pages/subcategories/DetailSubtoSub')));
